import { INeuroCommit, INeuroDocument } from 'neuro-data-structures';
import { omit } from 'Utility/ramdaReplacement';

import { getJWTData } from './jwtAuthTools';

const useruuid = getJWTData()?.useruuid;

export type TCommitControlProps = Pick<
  IControlProps,
  '_cid' | '_cdate' | '_editing' | '_commitsLength' | '_commitCreator'
>;

/**
 * Export data and control properties from latest selectable commit
 *
 * Note: Previously this required checking for user id, but document loading was changed so that
 *       unfinished commits from other users are filtered at loading time.
 * @param {INeuroCommit[]} commitArray - Array of commits
 * @return {object} Object of document data
 */
export const commitDataFromArray = (commitArray: INeuroCommit[]): TCommitControlProps | null => {
  let currentCommit: INeuroCommit | null = null;

  for (const commit of commitArray) {
    // Set the processed commit as currentCommit if there is no currentCommit, the processed commit is unfinished or
    // the the currentCommit is finished before the processed commit.
    if (
      !currentCommit ||
      commit.commitDate === null ||
      (currentCommit.commitDate !== null && currentCommit.commitDate < commit.commitDate)
    ) {
      currentCommit = commit;
    }
  }

  if (!currentCommit) return null;

  const returnCommit: TCommitControlProps = {
    ...(currentCommit.data as Record<string, TFieldValue>),
    _cid: currentCommit.commitId,
    _cdate: currentCommit.commitDate !== null ? currentCommit.commitDate : currentCommit.createDate,
    _commitCreator: currentCommit.creatorId,
    _editing: currentCommit.commitDate === null,
    _commitsLength: commitArray.length,
  };

  return returnCommit;
};

/**
 * Return latest commit data
 * @param {INeuroDocument} doc - Document
 * @return {document} Document data with control props included
 */
export const mergeDocument = (doc: INeuroDocument): TMergerReturnType => {
  // Remove unfinished commits from other users
  const filteredCommitArray = doc.commits.filter(c => !(c.commitDate === null && c.creatorId !== useruuid));

  const handledCommits: (TCommitControlProps & Record<string, TFieldValue>) | null =
    commitDataFromArray(filteredCommitArray);

  if (!handledCommits)
    return {
      _id: doc.documentId,
      _type: doc.documentType,
      _lockedFor: doc.lockedFor,
      _source: doc.source,
      _creator: doc.creatorId,
      _creatorOrg: doc.creatorOrg,
      _ownerOrg: doc.ownerOrg,
      _docCreateDate: doc.createDate,
      _pastOwners: doc.pastOwners,
      _cdate: 9999999999999, // Can't be undefined since it's used in sorting documents all over the project, so use a large number so that it is sorted to the top if needed
    };

  const returnData: TMergerReturnType = {
    ...handledCommits,
    _id: doc.documentId,
    _type: doc.documentType,
    _creator: doc.creatorId,
    _creatorOrg: doc.creatorOrg,
    _ownerOrg: doc.ownerOrg,
    _lockedFor: doc.lockedFor,
    _source: doc.source,
    _docCreateDate: doc.createDate,
    _pastOwners: doc.pastOwners,
  };

  return returnData;
};

/**
 * Get latest finished commit of all non-removed documents sorted by document
 * creation time (descending).
 *
 * @param {INeuroDocument[]} docArray - Array of documents
 * @return {document[]} Document data array with control props included
 */
export const sortAndMergeDocuments = (docArray: Array<INeuroDocument>): Array<TMergerReturnType> => {
  if (!Array.isArray(docArray)) return [];
  return docArray
    .reduce((acc: Array<TMergerReturnType>, cur) => {
      if (!Number.isInteger(cur.removeTS)) {
        acc.push(mergeDocument(cur));
      }
      return acc;
    }, [])
    .sort((a: { _docCreateDate: number }, b: { _docCreateDate: number }) => b._docCreateDate - a._docCreateDate);
};

export const controlProps: Array<keyof IControlProps> = [
  '_id',
  '_type',
  '_cid',
  '_cdate',
  '_editing',
  '_lockedFor',
  '_creator',
  '_creatorOrg',
  '_ownerOrg',
  '_source',
  '_commitsLength',
  '_docCreateDate',
  '_commitCreator',
  '_pastOwners',
];

/**
 * Remove control props from document data
 * @param  {{ [key: string]: any }} doc - Document with control props
 * @return {{ [key: string]: any }} Any document without control props
 */
export const omitControlProps = (
  doc: Partial<IControlProps>,
): Omit<Partial<IControlProps>, keyof IControlProps> & Record<string, TFieldValue> =>
  omit<Partial<IControlProps>, keyof IControlProps>(controlProps, doc);

/**
 * Get array of fields missing in a document
 * @param {Array<string>} fields list of fields wanted to check in the data
 * @param {IFormData['document']} docData Data in which the fields are checked to be found
 * @returns Array of the fields missing, being undefined or null
 */
export const getMissingFields = (fields: Array<string>, docData: IFormData['document']): Array<string> => {
  const missing: Array<string> = [];
  fields.forEach(f => {
    if (!(f in docData) || docData[f] === undefined || docData[f] === null) {
      missing.push(f);
    }
  });
  return missing;
};

/**
 * Test document source field to determine if the document originated from integrations
 * @param source source field string
 * @returns Whether the source is integration
 */
export const sourceIsIntegration = (source: INeuroDocument['source']) => {
  const re = new RegExp(/integration/, 'i');
  return !!(source || '').match(re);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isByInformant = (d: Record<string, any>, informants: Array<IInformant>): boolean => {
  if (d._source === 'irom') return true;
  if (!d?._creator) return false;

  return informants.some(inf => inf.mysqUserId === d._creator);
};
